/**
 * COACHDIA Pulse — Participant landing (Iter253 Phase 3)
 * =======================================================
 *
 * Page publique consommée via le magic link `/pulse-invite/:token`.
 *
 * 1. Valide le token via GET /api/team-pulse/invitations/{token}
 * 2. Affiche le welcome (inviter · batch · expiration) — aucune donnée des
 *    autres participants, aucun verbatim.
 * 3. CTA "Commencer mon test" → déclenche /start + redirige vers le flow
 *    Discovery standard en mode Pulse (submit câblé sur /submit Pulse).
 *
 * ⚠️ V1 : pour livrer Phase 3 rapidement, le test Pulse réutilise visuellement
 *    la page /discovery (moteur commun) mais la soumission est câblée côté
 *    backend sur l'endpoint Pulse. L'intégration complète test-native-Pulse
 *    est un chantier Phase 3.1 (backlog).
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Clock, Loader2, AlertCircle, ArrowRight, ShieldCheck } from 'lucide-react';
import { getApiUrl } from '../config/environment';

const API = getApiUrl();

export default function PulseInvite() {
  const { token } = useParams();
  const [state, setState] = useState('loading'); // loading | ready | invalid | expired | completed
  const [data, setData] = useState(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!token) { setState('invalid'); return; }
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/team-pulse/invitations/${token}`,
        );
        setData(data);
        if (data.invitation.status === 'completed') setState('completed');
        else setState('ready');
        // Fire-and-forget click tracker (passes landing)
        try {
          axios.post(
            `${API}/team-pulse/invitations/${token}/track-click`,
            { click_target: 'landing' },
          );
        } catch (_) { /* silent */ }
      } catch (err) {
        const s = err?.response?.status;
        if (s === 410) setState('expired');
        else setState('invalid');
      }
    })();
  }, [token]);

  const handleStart = async () => {
    setStarting(true);
    try {
      // Le démarrage effectif (transition not_started → in_progress) se fait
      // côté PulseTest pour éviter un double-appel lors de la redirection.
      window.location.href = `/pulse-test/${token}`;
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const msg = (typeof raw === 'string' && /[éèêàâîôûçùœ]/i.test(raw))
        ? raw : 'Impossible de démarrer le test pour le moment.';
      toast.error(msg);
      setStarting(false);
    }
  };

  // ─── Loading ───
  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-[#F5F1EA] flex items-center justify-center" data-testid="pulse-invite-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#92710A]" />
      </div>
    );
  }

  // ─── Invalid / Expired ───
  if (state === 'invalid' || state === 'expired') {
    return (
      <div className="min-h-screen bg-[#F5F1EA] flex items-center justify-center p-6" data-testid={`pulse-invite-${state}`}>
        <div className="max-w-md bg-white border border-[#EDE4D0] rounded-2xl p-10 text-center">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-stone-100 text-stone-500 mb-4">
            <AlertCircle className="h-5 w-5" />
          </div>
          <h1 className="text-2xl font-serif text-[#1F1A12] mb-2">
            {state === 'expired' ? 'Lien expiré' : 'Lien invalide'}
          </h1>
          <p className="text-sm text-[#4A3F2F]">
            {state === 'expired'
              ? 'Cette campagne est terminée. Contactez la personne qui vous a invité pour renouveler l\'accès.'
              : 'Le lien est incorrect ou a déjà été utilisé.'}
          </p>
        </div>
      </div>
    );
  }

  // ─── Completed ───
  if (state === 'completed') {
    return (
      <div className="min-h-screen bg-[#F5F1EA] flex items-center justify-center p-6" data-testid="pulse-invite-completed">
        <div className="max-w-md bg-white border border-[#EDE4D0] rounded-2xl p-10 text-center">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-emerald-50 text-emerald-700 mb-4">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <h1 className="text-2xl font-serif text-[#1F1A12] mb-2">Test déjà complété</h1>
          <p className="text-sm text-[#4A3F2F]">
            Merci, vos réponses ont bien été prises en compte.
          </p>
        </div>
      </div>
    );
  }

  // ─── Ready ───
  const { batch, invitation } = data || {};
  return (
    <div className="min-h-screen bg-[#F5F1EA]" data-testid="pulse-invite-ready">
      <div className="max-w-2xl mx-auto px-6 py-16 md:py-24">
        <div className="h-px w-16 bg-gradient-to-r from-[#92710A] to-[#D4AF37] mb-6" />
        <div className="text-[11px] tracking-[0.28em] uppercase text-[#92710A] font-medium inline-flex items-center gap-2">
          <Users className="h-3.5 w-3.5" /> COACHDIA Pulse · Invitation
        </div>
        <h1 className="mt-3 text-4xl md:text-5xl font-serif leading-[1.05] text-[#1F1A12]">
          {batch?.owner_display} vous invite
          <span className="block italic text-[#92710A] mt-2">
            au test d'équipe.
          </span>
        </h1>
        <p className="mt-6 text-base text-[#4A3F2F] leading-relaxed">
          Vous participez à <strong>« {batch?.name} »</strong>. Le test prend environ
          <strong> 10 minutes </strong>. Vos réponses servent uniquement à
          générer votre profil individuel et à alimenter le rapport d'équipe
          de façon anonyme.
        </p>

        <div className="mt-8 bg-white border border-[#EDE4D0] rounded-2xl p-6 md:p-8">
          <div className="flex items-start gap-3 mb-5">
            <div className="h-9 w-9 rounded-full bg-[#FAF6EE] border border-[#EDE4D0] flex items-center justify-center">
              <Clock className="h-4 w-4 text-[#92710A]" />
            </div>
            <div>
              <div className="text-sm text-[#1F1A12] font-medium">
                Expire le {new Date(batch?.expires_at).toLocaleDateString('fr-FR')}
              </div>
              <div className="text-xs text-[#8A7A5E] mt-0.5">
                Ne tardez pas — au-delà, la campagne est clôturée.
              </div>
            </div>
          </div>

          <button
            onClick={handleStart}
            disabled={starting}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#1F1A12] text-white px-7 py-4 rounded-full font-medium text-sm hover:bg-[#2A2319] transition disabled:opacity-40"
            data-testid="pulse-invite-start-btn"
          >
            {starting ? (
              <><Loader2 className="h-4 w-4 animate-spin" /> Préparation…</>
            ) : (
              <>Commencer mon test <ArrowRight className="h-4 w-4" /></>
            )}
          </button>
          <p className="mt-4 text-[11px] text-[#8A7A5E] text-center">
            Vos réponses restent confidentielles.
            Le rapport équipe est accessible uniquement au propriétaire de la campagne.
          </p>
        </div>
      </div>
    </div>
  );
}
